import React from "react";
import { SeverityLevel } from "@microsoft/applicationinsights-web";

import AxiosInstance from "api/axiosInstance.js";
import { extractSystemId } from "utils/utils.js";
import { API_STATUS_STRINGS } from "constants/constantStrings";
import { getAppInsights } from "components/Telemetry/TelemetryService";
import Logout from "pages/Logout";

import createNotification, { SUCCESS_NOTIFICATION, ERROR_NOTIFICATION } from "./../Notifications";
import "./index.css";

class ErrorBoundary extends React.Component {
  /** List of APIs for which don't need to handle error globally.
   * Component will take care of Error
   */
  excludeAPIs = ["polling", "download", "telemetry", "charts/format", "/insights/list", "/widgets/reorder"];
  //System specific URLs
  sysFullScreenErrURLs = ["dashboards", "charts", "alarms", "history/alarms", "tags", "reports"];
  //Other URLs
  otherFullScreenErrURLs = ["admin/users"];

  notificationExemptedScreensList = ["alarms", "notifications", "insights", "widgets"];

  constructor(props) {
    super(props);
    this.state = { hasError: false, isUnauthorized: false };
  }

  static getDerivedStateFromError = error => {
    console.log("getDerivedStateFromError - Error: ", error);
    return { hasError: true, isUnauthorized: false };
  };

  isFullPageError(url, method, status) {
    let isFullPageErr = false;
    if (status === 401) {
      this.setState({ ...this.state, isUnauthorized: true });
    } else {
      if (method === "get" && status !== 422) {
        let urlList = this.otherFullScreenErrURLs;
        let base = "";
        if (url.includes("/systems/")) {
          base = `/systems/${extractSystemId()}`;
          urlList = this.sysFullScreenErrURLs;
        }
        isFullPageErr = urlList.find(u => url.includes(`${base}/${u}`));
      }
    }
    return Boolean(isFullPageErr);
  }

  componentDidMount() {
    // Set axios interceptors
    this.requestInterceptor = AxiosInstance.interceptors.request.use(req => {
      this.setState({ hasError: false, isUnauthorized: false });
      return req;
    });

    this.responseInterceptor = AxiosInstance.interceptors.response.use(
      resp => {
        const { method, url } = resp.config;
        this.showSuccessMsg(method, url);
        return resp;
      },
      err => this.errorHandler(err)
    );
  }

  showSuccessMsg(method, url) {
    let showNotificationFlag = true;
    if (!this.isErrorNotInterceptedURL(url)) {
      const code = "Success";
      let description = null;
      switch (method) {
        case "delete":
        case "post":
          description = API_STATUS_STRINGS[method];
          break;
        case "put":
          if (!url.includes("time_window") && !url.includes("tags")) {
            description = API_STATUS_STRINGS[method];
          }
          showNotificationFlag = !this.notificationExemptedScreensList.some(screenName => url.includes(screenName));
          break;
        default:
          description = null;
      }

      if (showNotificationFlag && description) {
        createNotification(SUCCESS_NOTIFICATION, description, code);
      }
    }
  }

  isErrorNotInterceptedURL = url => this.excludeAPIs.some(api => url.includes(api));

  errorHandler = error => {
    const errResp = error.response || error;
    const { status } = errResp;
    const { url, method } = errResp.config;
    if (!this.isErrorNotInterceptedURL(url)) {
      // const hasError = this.isFullPageError(url, method, status);
      // this.setState({ hasError });
      // if (!hasError) {
      const errData = errResp.data;
      const code = (errData && errData.Code) || "Error";
      let description = (errData && errData.detail) || API_STATUS_STRINGS.fail;
      if (status === 401 || status === 403) {
        this.setState({ isUnauthorized: true });
        description = API_STATUS_STRINGS.unAuthorized;
      } else if (status === 500 || status === 502 || status === 503 || status === 504) {
        description = API_STATUS_STRINGS.fail;
      }
      console.log("Status.." + status);

      createNotification(ERROR_NOTIFICATION, description, code);

      //}
    }
    throw error;
  };

  componentDidCatch(error, errorInfo) {
    const insightObj = getAppInsights();
    if (insightObj) {
      insightObj.trackException({
        error: error,
        exception: error,
        severityLevel: SeverityLevel.Error,
        properties: { ...errorInfo }
      });
    }
  }

  componentWillUnmount() {
    AxiosInstance.interceptors.request.eject(this.requestInterceptor);
    AxiosInstance.interceptors.response.eject(this.responseInterceptor);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="app-wrapper text-center">
          <div className="error-container">
            Sorry.
            <br />
            This page is unavailable now, please try again later.
          </div>
          {/* <Link className="whiteText homeButton" to="/">HOME</Link> */}
        </div>
      );
    }
    return <>{this.state.isUnauthorized ? <Logout /> : this.props.children}</>;
  }
}

export default ErrorBoundary;
